import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Wordmark } from '@wundo/ui';
import { ApiError } from '@wundo/client/api.ts';
import type { SignInDestination } from '@wundo/client/auth.ts';
import { leaveTo } from '@wundo/client/leaveTo.ts';
import { SigningIn } from '@/components/SigningIn.tsx';

export interface SignInLandingProps {
  title: string;
  body: string;
  button: string;
  /** Spends the token or the pass and answers where to go. Never called on render. */
  action: () => Promise<SignInDestination>;
  /** The backend's reason for refusing, or null when it gave none. */
  onRefused: (code: string | null) => void;
  /** Act on mount instead of waiting for a press; only for a link the person asked for. */
  autoStart?: boolean;
}

/**
 * The screen an emailed or chat link lands on: it waits for a person to press
 * the button, and only then spends what the link carries.
 *
 * The first thing to open a link in a corporate mailbox is not the person.
 * Scanners fetch every URL in an incoming mail, and Microsoft's sandbox goes
 * further — it replays the addresses a browser navigated to, from its own
 * machine, minutes later. So the button must not navigate anywhere: it posts,
 * and a POST is not in the history there is to replay.
 *
 * Nothing about the person or the errand is on this page, because whatever
 * replays the link sees it too.
 */
export function SignInLanding({
  title,
  body,
  button,
  action,
  onRefused,
  autoStart = false,
}: SignInLandingProps) {
  const [leaving, setLeaving] = useState(autoStart);
  const started = useRef(false);

  const start = useCallback(() => {
    if (started.current) {
      return;
    }
    started.current = true;
    setLeaving(true);

    action()
      .then(({ destination }) => {
        leaveTo(destination);
      })
      .catch((caught: unknown) => {
        onRefused(caught instanceof ApiError ? caught.code : null);
      });
  }, [action, onRefused]);

  useEffect(() => {
    if (autoStart) {
      start();
    }
  }, [autoStart, start]);

  if (leaving) {
    return <SigningIn />;
  }

  return (
    <main className='flex h-dvh items-center justify-center px-7'>
      <div className='flex w-full max-w-98 flex-col items-center gap-5 text-center'>
        <Wordmark registered className='h-8.5' />
        <div className='flex flex-col gap-1.5'>
          <h1 className='text-xl font-semibold tracking-tight'>{title}</h1>
          <p className='text-md text-ink-muted'>{body}</p>
        </div>
        <Button
          type='button'
          variant='accent'
          shape='block'
          size='lg'
          fullWidth
          onClick={start}
        >
          {button}
        </Button>
      </div>
    </main>
  );
}
